/**
 * Utility functions for working with sessionStorage
 * Provides a consistent API for storing, retrieving, and clearing session data
 */
export const sessionStorage = {
  /**
   * Store a value in sessionStorage with JSON serialization
   * @param key - The storage key
   * @param value - The value to store
   */
  setItem: (key: string, value: any): void => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error saving to sessionStorage:', error);
    }
  },
  
  /**
   * Retrieve a value from sessionStorage with JSON parsing
   * @param key - The storage key
   * @param defaultValue - Default value if key doesn't exist
   * @returns The stored value or defaultValue
   */
  getItem: <T>(key: string, defaultValue: T | null = null): T | null => {
    try {
      const item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error('Error reading from sessionStorage:', error);
      return defaultValue;
    }
  },
  
  /**
   * Remove an item from sessionStorage
   * @param key - The storage key to remove
   */
  removeItem: (key: string): void => {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing from sessionStorage:', error);
    }
  },
  
  /**
   * Clear all items from sessionStorage
   */
  clear: (): void => {
    try {
      window.sessionStorage.clear();
    } catch (error) {
      console.error('Error clearing sessionStorage:', error);
    }
  },
  
  /**
   * Clear all VeriPay-specific data from sessionStorage
   * This preserves any non-VeriPay data that might be in sessionStorage
   */
  clearAllAppData: (): void => {
    try {
      // Get all keys in sessionStorage
      const keys = Object.keys(window.sessionStorage);
      
      // Remove all keys that start with 'veripay_' or are token-related
      keys.forEach(key => {
        if (key.startsWith('veripay_') ||
            key === 'token' ||
            key === 'access_token' ||
            key === 'id_token') {
          window.sessionStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing app data from sessionStorage:', error);
    }
  }
};

// Constants for common storage keys
export const STORAGE_KEYS = {
  TOKEN: 'token', // Legacy token key
  ACCESS_TOKEN: 'access_token', // New key for access token
  ID_TOKEN: 'id_token', // New key for ID token
  ROUTE: 'veripay_route',
  STATE: 'veripay_state',
  TRANSACTIONS: 'veripay_transactions',
  UI_STATE: 'veripay_ui_state',
  NOTIFICATIONS: 'veripay_notifications',
  TRACKING: 'veripay_tracking',
  AUTH_IN_PROGRESS: 'veripay_auth_in_progress'
};

/**
 * Authentication state tracking utilities
 */
export const authStateTracking = {
  /**
   * Set authentication in progress flag
   */
  setAuthInProgress: (): void => {
    sessionStorage.setItem(STORAGE_KEYS.AUTH_IN_PROGRESS, true);
  },

  /**
   * Clear authentication in progress flag
   */
  clearAuthInProgress: (): void => {
    sessionStorage.removeItem(STORAGE_KEYS.AUTH_IN_PROGRESS);
  },

  /**
   * Check if authentication is in progress
   * @returns boolean indicating if authentication is in progress
   */
  isAuthInProgress: (): boolean => {
    return !!sessionStorage.getItem(STORAGE_KEYS.AUTH_IN_PROGRESS);
  }
};